/**
 * MULTIPLAYER LOCAL — split-screen seats for 2-4 players on one machine.
 *
 * Each extra seat (P2-P4) is a REAL Colyseus session: its own guest token,
 * its own websocket, its own server-side player. The server needs nothing
 * new — a seat is just another client in P1's private room (it drops into
 * the running match via ClientMsg.LobbyJoinLive, exactly like a friend
 * joining mid-game). What IS local: each seat samples its own claimed
 * gamepad, runs the same shared-movement prediction/reconcile the main
 * client runs (mini version of match.ts's loop), and renders through one
 * scissor viewport of the SHARED renderer (renderer.localViews — never a
 * second WebGL context).
 *
 * Lifecycles, because they differ and it matters:
 *  - The SESSION (seat connections) lives from the local-lobby START until
 *    P1 leaves the room to the menu — it survives round restarts and P1's
 *    seamless per-round reconnects (aux sockets are guests with unique
 *    usernames, so the server's same-account stale-bump never touches them).
 *  - The VIEWS (camera + prediction + HUD) are per-MatchMode, rebuilt each
 *    round by match.ts and disposed in its exit path.
 */
import type { Room } from "colyseus.js";
import * as THREE from "three";
import {
  ARENA_CAM,
  arenaKartTravel,
  Buttons,
  ClientMsg,
  WEAPONS,
  applyPlatformCarry,
  eyeHeight,
  loadoutSpeedMul,
  modBounce,
  modGravityMul,
  modSpeedMul,
  spawnKinematics,
  stepGlide,
  stepPlayer,
  type CollisionWorld,
  type PlayerInput,
  type PlayerKinematics,
} from "@neon-purge/shared";
import { claimPad, releasePad, loadPadBinds, loadSensitivity, loadBehavior, rightStick } from "./input.js";
import { ViewModel } from "./viewmodel.js";
import { joinAsLocalGuest } from "../net/game.js";
import { el } from "../ui/dom.js";

const SIM_DT_MS = 1000 / 60;
const PITCH_LIMIT = Math.PI / 2 - 0.01;

/** Viewport rectangles for n total players (P1 first). Normalized, y from the
 *  top — the same convention renderer.localViews and the HUD overlays use.
 *  2 players stack horizontally (couch classic); 3-4 take quadrants. */
export function seatRects(n: number): { x: number; y: number; w: number; h: number }[] {
  if (n <= 1) return [{ x: 0, y: 0, w: 1, h: 1 }]; // lone pad seat: fullscreen
  if (n === 2) {
    return [
      { x: 0, y: 0, w: 1, h: 0.5 },
      { x: 0, y: 0.5, w: 1, h: 0.5 },
    ];
  }
  return [
    { x: 0, y: 0, w: 0.5, h: 0.5 },
    { x: 0.5, y: 0, w: 0.5, h: 0.5 },
    { x: 0, y: 0.5, w: 0.5, h: 0.5 },
    { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
  ].slice(0, n);
}

/** Fixed seat accents — they paint the avatar body/glow AND the seat's HUD,
 *  so "I'm the green one" reads across the whole screen. Indexed by slot;
 *  the player NUMBER is separate (seat.label) because it shifts when the
 *  keyboard player is removed (first pad becomes P1). */
export const SEAT_STYLE = [
  { body: "#b3402a", glow: "#ff5a3c" },
  { body: "#2a8a44", glow: "#4cff70" },
  { body: "#a8862a", glow: "#ffd23c" },
  { body: "#5b3a9e", glow: "#b06cff" },
] as const;

export interface LocalSeat {
  /** Gamepad index this seat owns (claimed for the duration of a match). */
  padIndex: number;
  padId: string;
  /** 0-based pad-seat slot (drives the accent color). */
  slot: number;
  /** Display label ("P2"… — or "P1"… when the keyboard player is off). */
  label: string;
}

export interface SeatConn {
  seat: LocalSeat;
  /** Live aux session; null while connecting or after a drop. */
  room: Room | null;
  /** Human-readable connection problem ("" = fine). */
  error: string;
  /** Monotonic input seq for THIS connection (never reset — the server
   *  zeroes its own lastSeq each round, so a climbing seq always passes). */
  seq: number;
}

interface LocalSession { roomId: string; seats: SeatConn[]; keyboard: boolean }

let session: LocalSession | null = null;

/** Open one guest session per seat into P1's room. Fire-and-forget per seat:
 *  a seat that fails to connect shows its error on its viewport HUD instead
 *  of blocking the others.
 *
 *  Pads are NOT claimed here — LocalSeatView claims per match and releases
 *  per round, so a seated pad can still drive padMenuLoop through the local
 *  lobby, the loading screen and the between-rounds room lobby. Claiming for
 *  a whole session locked the menus for anyone whose only pad was seated. */
export function startLocalSession(roomId: string, seats: LocalSeat[], keyboard: boolean): void {
  endLocalSession();
  const conns: SeatConn[] = seats.map((seat) => ({ seat, room: null, error: "", seq: 1 }));
  session = { roomId, seats: conns, keyboard };
  for (const conn of conns) {
    const style = SEAT_STYLE[conn.seat.slot] ?? SEAT_STYLE[0];
    void joinAsLocalGuest(roomId, { bodyColor: style.body, glowColor: style.glow })
      .then((room) => {
        // the session may have ended while this seat was still handshaking
        if (session?.seats.includes(conn)) {
          conn.room = room;
          room.onLeave(() => { if (conn.room === room) { conn.room = null; conn.error = "connection lost"; } });
          room.onError(() => { if (conn.room === room) { conn.room = null; conn.error = "connection lost"; } });
        } else {
          void room.leave();
        }
      })
      .catch((err) => { conn.error = (err as Error).message || "couldn't join"; });
  }
}

/** The seat connections for a room ([] when this isn't a local match). */
export function localSeats(roomId: string): SeatConn[] {
  return session && session.roomId === roomId ? session.seats : [];
}

export function localSessionActive(): boolean {
  return session !== null;
}

/** false = the couch match runs WITHOUT a keyboard player: the owner session
 *  still hosts the room but retreats to pending (never a combatant) and only
 *  the pad seats get viewports. true outside local sessions. */
export function localKeyboardOn(): boolean {
  return session?.keyboard ?? true;
}

/** Tear the whole local session down (P1 left the room / back to menu). */
export function endLocalSession(): void {
  if (!session) return;
  for (const conn of session.seats) {
    releasePad(conn.seat.padIndex);
    const r = conn.room;
    conn.room = null;
    if (r) { try { void r.leave(); } catch { /* already closed */ } }
  }
  session = null;
}

/** Per-viewport HUD: the few in-world facts a couch player needs, drawn as a
 *  DOM overlay clipped to their quadrant. Deliberately tiny — the full Hud
 *  class is a singleton with global side effects (uiRoot ownership, audio,
 *  chat) and P1 keeps it. */
class SeatHud {
  readonly root: HTMLElement;
  private hpFill: HTMLElement;
  private hpText: HTMLElement;
  private weaponEl: HTMLElement;
  private statusEl: HTMLElement;
  private nameEl: HTMLElement;
  private scoreEl: HTMLElement;
  private lastSig = "";

  constructor(rect: { x: number; y: number; w: number; h: number }, accent: string, label: string) {
    this.root = el("div", {
      class: "seat-hud",
      style:
        `position:absolute;left:${rect.x * 100}%;top:${rect.y * 100}%;` +
        `width:${rect.w * 100}%;height:${rect.h * 100}%;` +
        "pointer-events:none;box-sizing:border-box;border:1px solid rgba(255,255,255,0.18);" +
        "font-family:inherit;overflow:hidden;z-index:5;",
    });
    const cross = el("div", {
      style:
        "position:absolute;left:50%;top:50%;width:10px;height:10px;transform:translate(-50%,-50%);" +
        `color:${accent};font:700 14px monospace;text-align:center;line-height:10px;` +
        "text-shadow:0 0 4px rgba(0,0,0,0.8);",
      text: "+",
    });
    this.nameEl = el("div", {
      style:
        `position:absolute;left:8px;top:6px;color:${accent};font-size:0.7rem;letter-spacing:0.12em;` +
        "text-shadow:0 1px 3px rgba(0,0,0,0.9);",
      text: label,
    });
    this.scoreEl = el("div", {
      style:
        "position:absolute;right:8px;top:6px;color:#eaf6ff;font-size:0.7rem;letter-spacing:0.1em;" +
        "text-shadow:0 1px 3px rgba(0,0,0,0.9);",
    });
    const hpWrap = el("div", {
      style:
        "position:absolute;left:8px;bottom:8px;width:130px;height:10px;" +
        "background:rgba(0,0,0,0.55);border:1px solid rgba(255,255,255,0.35);",
    });
    this.hpFill = el("div", { style: `height:100%;width:100%;background:${accent};transition:width 0.12s;` });
    hpWrap.append(this.hpFill);
    this.hpText = el("div", {
      style:
        "position:absolute;left:8px;bottom:22px;color:#eaf6ff;font-size:0.65rem;" +
        "text-shadow:0 1px 3px rgba(0,0,0,0.9);",
    });
    this.weaponEl = el("div", {
      style:
        "position:absolute;right:8px;bottom:8px;color:#eaf6ff;font-size:0.7rem;letter-spacing:0.12em;" +
        "text-transform:uppercase;text-shadow:0 1px 3px rgba(0,0,0,0.9);",
    });
    this.statusEl = el("div", {
      style:
        "position:absolute;left:50%;top:42%;transform:translate(-50%,-50%);color:#ffb3c2;" +
        "font-size:0.95rem;letter-spacing:0.2em;text-align:center;" +
        "text-shadow:0 0 8px rgba(0,0,0,0.9);white-space:nowrap;",
    });
    this.root.append(cross, this.nameEl, this.scoreEl, hpWrap, this.hpText, this.weaponEl, this.statusEl);
  }

  sync(p: { hp: number; maxHp: number; weapon: string; ammo: number; kills: number; deaths: number; username: string } | null, status: string): void {
    const sig = p
      ? `${p.hp}/${p.maxHp}|${p.weapon}|${p.ammo}|${p.kills}|${p.deaths}|${p.username}|${status}`
      : `none|${status}`;
    if (sig === this.lastSig) return;
    this.lastSig = sig;
    if (p) {
      const frac = p.maxHp > 0 ? Math.max(0, Math.min(1, p.hp / p.maxHp)) : 0;
      this.hpFill.style.width = `${Math.round(frac * 100)}%`;
      this.hpText.textContent = `${p.hp} HP`;
      this.weaponEl.textContent = `${p.weapon}${p.ammo > 0 ? ` · ${p.ammo}` : ""}`;
      this.scoreEl.textContent = `${p.kills} / ${p.deaths}`;
      if (this.nameEl.textContent?.indexOf("·") === -1 && p.username) {
        this.nameEl.textContent = `${this.nameEl.textContent} · ${p.username}`;
      }
    }
    this.statusEl.textContent = status;
  }
}

/** One seat's per-round runtime: pad sampling → prediction → camera + HUD.
 *  A deliberately small mirror of match.ts's input/prediction path — the
 *  simplifications (no viewmodel, no sub-step camera offset, no UFO/worm/
 *  dragon piloting, no killcam) are all cosmetic; movement itself runs the
 *  SAME shared stepPlayer/stepGlide + reconcile-per-ack pattern, so a seat
 *  feels as tight as the main player on a LAN/localhost server. */
export class LocalSeatView {
  readonly camera: THREE.PerspectiveCamera;
  readonly hud: SeatHud;
  /** First-person arms + gun, on layer 3+slot so ONLY this seat's camera
   *  renders it. The match adds this.camera to the scene (a viewmodel is a
   *  camera child); the per-round renderer sweep disposes it with the scene. */
  readonly viewModel: ViewModel;
  yaw = 0;
  pitch = 0;
  /** Own-fire detection off replicated state (the seat's own connection never
   *  receives its own Shot broadcast): an ammo drop = a round left the barrel. */
  private lastAmmo = -1;
  private lastRocketAmmo = -1;
  private lastVmWeapon = "";

  private kin: PlayerKinematics | null = null;
  private pendingInputs: PlayerInput[] = [];
  private lastReconciledSeq = -1;
  private renderErr = { x: 0, y: 0, z: 0 };
  private accMs = 0;
  private frameDtMs = SIM_DT_MS;
  /** ARENA (MKDD) chase-cam yaw for this seat (null = not in an arena kart). */
  private mkddCamYaw: number | null = null;
  private mkddTrv = { speed: 0, head: 0 };
  private wasAlive = false;
  private nextJoinLiveAt = 0;

  // pad state (per seat — the global InputManager must never see this pad)
  private padBinds = loadPadBinds();
  private sens = loadSensitivity();
  private behavior = loadBehavior();
  private prevPressed: boolean[] = [];
  private adsHeld = false;
  private crouchToggled = false;
  private meleeTap = false;
  private grenadeTap = false;
  private healTap = false;
  private lookX = 0;
  private lookY = 0;

  constructor(readonly conn: SeatConn, readonly rect: { x: number; y: number; w: number; h: number }) {
    const style = SEAT_STYLE[conn.seat.slot] ?? SEAT_STYLE[0];
    this.camera = new THREE.PerspectiveCamera(loadBehavior().fov ?? 90, rect.w / rect.h, 0.05, 600);
    this.camera.rotation.order = "YXZ";
    this.camera.layers.enable(1); // world layers only — never P1's FP viewmodel (2)
    // own arms/gun on a per-seat layer (the ViewModel ctor enables it on the cam)
    this.viewModel = new ViewModel(this.camera, 3 + conn.seat.slot);
    this.viewModel.baseFov = this.camera.fov;
    this.hud = new SeatHud(rect, style.glow, conn.seat.label);
    // claimed for THIS round only: in-match the pad must be invisible to the
    // main InputManager/rumble, but between rounds it has to keep driving the
    // menu pad-nav (room lobby, pause) — dispose() releases it
    claimPad(conn.seat.padIndex);
  }

  /** This seat's server-side session id ("" while connecting). */
  get sessionId(): string {
    return this.conn.room?.sessionId ?? "";
  }

  private pad(): Gamepad | null {
    const gp = navigator.getGamepads?.()[this.conn.seat.padIndex] ?? null;
    return gp?.connected ? gp : null;
  }

  private me(): any {
    const room = this.conn.room;
    if (!room) return null;
    return (room.state as unknown as { players?: { get: (k: string) => unknown } }).players?.get?.(room.sessionId) ?? null;
  }

  private speedMul(p: any): number {
    const st = this.conn.room?.state as unknown as { rules?: { modifier?: string } } | undefined;
    return modSpeedMul(st?.rules?.modifier ?? "", Date.now() < (p?.speedUntil ?? 0)) * loadoutSpeedMul(p?.loadout);
  }

  private antigravMul(): number {
    const st = this.conn.room?.state as unknown as { rules?: { modifier?: string }; antigravUntil?: number } | undefined;
    return modGravityMul(st?.rules?.modifier ?? "", Date.now() < (st?.antigravUntil ?? 0));
  }

  /** RUBBER modifier restitution — same replicated scalar the server passes, so
   *  this seat's prediction + reconcile bounce exactly as the authority does. */
  private bounceMul(): number {
    const st = this.conn.room?.state as unknown as { rules?: { modifier?: string } } | undefined;
    return modBounce(st?.rules?.modifier ?? "");
  }

  /** Per-frame: look integration + button edge latching (mirrors the shape of
   *  InputManager.updateGamepad / pollGamepadEdges for ONE fixed pad). */
  private pollPad(gp: Gamepad, dtSec: number, driving: boolean): void {
    const pb = this.padBinds;
    const btnVal = (i: number): number => gp.buttons[i]?.value ?? (gp.buttons[i]?.pressed ? 1 : 0);
    const pressed = (i: number): boolean => !!gp.buttons[i]?.pressed || btnVal(i) > 0.5;

    // edge latches (consumed by the next sim step's sample)
    const edge = (i: number): boolean => pressed(i) && !this.prevPressed[i];
    if (edge(pb.melee)) this.meleeTap = true;
    if (edge(pb.grenade) && !driving) this.grenadeTap = true;
    if (edge(pb.heal)) this.healTap = true;
    if (!this.behavior.crouchHold && edge(pb.crouch)) this.crouchToggled = !this.crouchToggled;

    // ADS with enter/leave hysteresis (trigger flutter — same constants as P1)
    const adsV = btnVal(pb.ads);
    this.adsHeld = this.adsHeld ? adsV > 0.18 || pressed(pb.ads) : adsV > 0.32 || !!gp.buttons[pb.ads]?.pressed;

    // right-stick look: radial deadzone + rate filter + response curve —
    // constants copied from InputManager.updateGamepad so both feels match
    const rs = rightStick(gp);
    const rmag = Math.hypot(rs.x, rs.y);
    const rDead = 0.24;
    const rScale = rmag > rDead ? Math.min(1, (rmag - rDead) / (1 - rDead)) / Math.max(rmag, 1e-6) : 0;
    const filterHz = this.adsHeld ? 22 : 32;
    const k = 1 - Math.exp(-Math.max(0, dtSec) * filterHz);
    this.lookX += (rs.x * rScale - this.lookX) * k;
    this.lookY += (rs.y * rScale - this.lookY) * k;
    const adsMul = this.adsHeld ? Math.min(0.45, this.sens.adsMult) : 1;
    const speed = (4.0 + this.sens.mouse * 500) * adsMul;
    const curve = (v: number): number => Math.sign(v) * Math.pow(Math.abs(v), 1.35);
    this.yaw -= curve(this.lookX) * speed * dtSec;
    const dpitch = curve(this.lookY) * speed * dtSec * (this.sens.invertY ? -1 : 1);
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch - dpitch));

    this.prevPressed = gp.buttons.map((b) => b.pressed || b.value > 0.5);
  }

  /** One sim step's input tuple off the held pad state. */
  private sample(gp: Gamepad | null, driving: boolean, p: any): { moveX: number; moveZ: number; buttons: number } {
    if (!gp) return { moveX: 0, moveZ: 0, buttons: 0 };
    const pb = this.padBinds;
    const btn = (i: number): boolean => !!gp.buttons[i]?.pressed || (gp.buttons[i]?.value ?? 0) > 0.5;
    const dz = (v: number): number => (Math.abs(v) < 0.18 ? 0 : (v - Math.sign(v) * 0.18) / 0.82);
    const moveX = dz(gp.axes[0] ?? 0);
    const moveZ = -dz(gp.axes[1] ?? 0);

    let b = 0;
    if (btn(pb.fire)) b |= Buttons.Fire;
    if (this.adsHeld) b |= Buttons.ADS;
    const jumpHeld = btn(pb.jump);
    if (!driving && jumpHeld) { b |= Buttons.Jump; this.crouchToggled = false; }
    const crouch = this.behavior.crouchHold ? btn(pb.crouch) : this.crouchToggled;
    if (crouch && !driving) b |= Buttons.Crouch;
    if (btn(pb.sprint)) b |= Buttons.Sprint;
    if (btn(pb.reload)) b |= Buttons.Reload;
    if (btn(pb.use)) b |= Buttons.Use;
    if (driving) {
      // fixed vehicle layout, same as P1's pad: L2 ascend, L1 descend
      if (btn(6)) b |= Buttons.Jump;
      if (btn(4)) b |= Buttons.Crouch;
    } else if (btn(4)) {
      b |= Buttons.Grapple; // L1/LB on foot = grappling hook
    }
    // parachute/glide: server honors the Glide bit only while airborne. Hold
    // jump to deploy (or keep flying a jetpack); a fresh drop-in's parachute
    // state forces it so prediction matches the server's slow descent.
    const airborne = this.kin ? !this.kin.onGround : false;
    if ((p?.parachute || (jumpHeld && airborne && ((this.kin?.vy ?? 0) < -3 || p?.hasJetpack))) && !driving) {
      b |= Buttons.Glide;
    }
    if (this.meleeTap) { b |= Buttons.Knife; this.meleeTap = false; }
    if (this.grenadeTap) { b |= Buttons.Grenade; this.grenadeTap = false; }
    if (this.healTap) { b |= Buttons.Heal; this.healTap = false; }
    return { moveX, moveZ, buttons: b };
  }

  /** Mirror of match.ts's reconcile, minus the piloting special cases a seat
   *  can't enter (UFO helm/worm/dragon are Use-driven and still work — they
   *  fall under `driving`, where prediction is off entirely). */
  private reconcile(server: any, world: CollisionWorld): void {
    if (!this.kin) return;
    while (this.pendingInputs.length && this.pendingInputs[0].seq <= server.lastSeq) this.pendingInputs.shift();
    if (!server.alive) return;
    const replayed: PlayerKinematics = {
      x: server.x, y: server.y, z: server.z,
      vx: server.vx, vy: server.vy, vz: server.vz,
      onGround: server.onGround, crouching: server.crouching,
      // zip/grapple aren't replicated — carry the locally predicted state
      // (same reasoning as match.ts reconcile, same fields)
      zipIdx: this.kin.zipIdx ?? -1,
      zipDist: this.kin.zipDist ?? 0,
      zipStart: this.kin.zipStart ?? 0,
      zipCd: this.kin.zipCd ?? 0,
      grapOn: this.kin.grapOn ?? false,
      grapX: this.kin.grapX, grapY: this.kin.grapY, grapZ: this.kin.grapZ,
    };
    const hasJetpack = !!server.hasJetpack;
    // per-render-frame platform deltas replayed at fixed sim dt need the same
    // rescale the main player uses (invariant 3 — frame rate vs sim step)
    const carryScale = Math.min(6, Math.max(0.05, SIM_DT_MS / Math.max(1, this.frameDtMs)));
    for (const input of this.pendingInputs) {
      applyPlatformCarry(world, replayed, carryScale);
      const rGlide = (input.buttons & Buttons.Glide) !== 0 && !replayed.onGround;
      if (rGlide && hasJetpack) stepGlide(replayed, input, world);
      else stepPlayer(replayed, input, world, this.speedMul(server), rGlide && !hasJetpack, this.antigravMul(), this.bounceMul(), Date.now() < (server.cloakUntil ?? 0));
    }
    const err = Math.hypot(replayed.x - this.kin.x, replayed.y - this.kin.y, replayed.z - this.kin.z);
    if (err > 0.001) {
      if (err < 1.2) {
        this.renderErr.x += this.kin.x - replayed.x;
        this.renderErr.y += this.kin.y - replayed.y;
        this.renderErr.z += this.kin.z - replayed.z;
        const m = Math.hypot(this.renderErr.x, this.renderErr.y, this.renderErr.z);
        if (m > 1.2) { const s = 1.2 / m; this.renderErr.x *= s; this.renderErr.y *= s; this.renderErr.z *= s; }
      } else {
        this.renderErr.x = this.renderErr.y = this.renderErr.z = 0;
      }
      this.kin = replayed;
    }
  }

  /** Per render frame, from the match's frame loop. */
  update(dtMs: number, world: CollisionWorld): void {
    this.frameDtMs = Math.max(1, dtMs);
    const dtSec = dtMs / 1000;
    const room = this.conn.room;
    if (!room) {
      this.hud.sync(null, this.conn.error || "connecting…");
      return;
    }
    const p = this.me();
    if (!p) { this.hud.sync(null, "joining…"); return; }

    // pending seat: ask to drop in (parachute), retry until the server seats us
    if (p.pending) {
      const now = performance.now();
      if (now >= this.nextJoinLiveAt) {
        this.nextJoinLiveAt = now + 1500;
        room.send(ClientMsg.LobbyJoinLive);
      }
    }

    const gp = this.pad();
    const driving = !!p.vehicleId;
    const alive = !!p.alive && !p.pending;

    // (re)spawn: adopt the server position and drop stale prediction
    if ((alive && !this.wasAlive) || !this.kin) {
      this.kin = spawnKinematics([p.x, p.y, p.z]);
      this.pendingInputs.length = 0;
      this.lastReconciledSeq = p.lastSeq ?? -1;
      this.renderErr.x = this.renderErr.y = this.renderErr.z = 0;
      this.accMs = 0;
      this.yaw = p.yaw ?? this.yaw;
      this.pitch = 0;
    }
    this.wasAlive = alive;

    if (gp) this.pollPad(gp, dtSec, driving);

    // moving platforms carry the standing body once per render frame (same
    // call site shape as the main player: BEFORE the sim steps)
    if (alive && !driving && this.kin) applyPlatformCarry(world, this.kin);

    // fixed-step sim + send; a background-tab burst is dropped, not replayed
    this.accMs += dtMs;
    if (this.accMs > 250) this.accMs = SIM_DT_MS;
    while (this.accMs >= SIM_DT_MS) {
      this.accMs -= SIM_DT_MS;
      const s = this.sample(gp, driving, p);
      const input: PlayerInput = {
        seq: this.conn.seq++,
        dtMs: SIM_DT_MS,
        moveX: s.moveX,
        moveZ: s.moveZ,
        yaw: this.yaw,
        pitch: this.pitch,
        buttons: s.buttons,
      };
      if (alive && !driving && this.kin) {
        const gliding = (input.buttons & Buttons.Glide) !== 0 && !this.kin.onGround;
        if (gliding && p.hasJetpack) stepGlide(this.kin, input, world);
        else stepPlayer(this.kin, input, world, this.speedMul(p), gliding && !p.hasJetpack, this.antigravMul(), this.bounceMul(), Date.now() < (p.cloakUntil ?? 0));
      }
      this.pendingInputs.push(input);
      while (this.pendingInputs.length > 180) this.pendingInputs.shift();
      room.send(ClientMsg.Input, input);
    }

    // reconcile once per server ack (poll lastSeq — never per-item onChange,
    // and never per frame: both failure modes are documented in CLAUDE.md)
    if (alive && !driving && p.lastSeq !== this.lastReconciledSeq) {
      this.lastReconciledSeq = p.lastSeq;
      this.reconcile(p, world);
    }

    // camera: predicted body + decaying reconcile offset; server pose when
    // the server owns the position (vehicle, dead, pending)
    const dk = Math.exp(-8 * dtSec);
    this.renderErr.x *= dk; this.renderErr.y *= dk; this.renderErr.z *= dk;
    if (alive && !driving && this.kin) {
      this.camera.position.set(
        this.kin.x + this.renderErr.x,
        this.kin.y + eyeHeight(this.kin) + this.renderErr.y,
        this.kin.z + this.renderErr.z,
      );
    } else if (driving || p.pending) {
      this.camera.position.set(p.x, p.y + 1.4, p.z);
    }
    // ARENA kart: the same MKDD chase rig as P1 (match.ts) on the server
    // pose — rigid 2.75 KL boom, 13° down, yaw smoothed toward the TRAVEL.
    const st = room.state as unknown as { mode?: string; vehicles?: { get: (k: string) => any } };
    const kv = driving && st.mode === "arena" ? st.vehicles?.get?.(p.vehicleId) : null;
    if (kv && kv.kind === "bombkart") {
      arenaKartTravel(kv, this.mkddTrv);
      if (this.mkddCamYaw === null) this.mkddCamYaw = this.mkddTrv.head;
      else if (Math.abs(this.mkddTrv.speed) > ARENA_CAM.holdBelow && !((kv.spinUntil ?? 0) > Date.now())) {
        let dv = this.mkddTrv.head - this.mkddCamYaw;
        while (dv > Math.PI) dv -= 2 * Math.PI;
        while (dv < -Math.PI) dv += 2 * Math.PI;
        this.mkddCamYaw += dv * (1 - Math.pow(1 - ARENA_CAM.yawGain, dtSec * 60));
      }
      const split = this.camera.aspect > 2;
      const lookH = split ? ARENA_CAM.lookSplit : ARENA_CAM.lookFull;
      const cy = this.mkddCamYaw, cp = Math.cos(ARENA_CAM.pitch), sp = Math.sin(ARENA_CAM.pitch);
      this.camera.position.set(kv.x + Math.sin(cy) * cp * ARENA_CAM.boom, kv.y + lookH + sp * ARENA_CAM.boom, kv.z + Math.cos(cy) * cp * ARENA_CAM.boom);
      this.camera.up.set(0, 1, 0);
      this.camera.lookAt(kv.x, kv.y + lookH, kv.z);
      this.yaw = cy;
      this.viewModel.overrideFov = split ? ARENA_CAM.fovSplit : ARENA_CAM.fovFull;
    } else {
      if (this.mkddCamYaw !== null) this.viewModel.overrideFov = null;
      this.mkddCamYaw = null;
      // dead: freeze where we fell (server-side respawn flips `alive` back)
      this.camera.rotation.set(this.pitch, this.yaw, 0);
    }

    // ---- first-person arms/gun (mirrors the match's viewmodel drive, lean
    // version: no ladder prewarm, no scope/kart FOV specials) ----
    const vm = this.viewModel;
    vm.setSkin(p.bodyColor, p.glowColor, p.skinUrl || undefined); // sig-guarded internally
    const vmWeapon = (p.rocketAmmo ?? 0) > 0 ? "rocketlauncher"
      : p.hasImperion ? "imperion"
      : (p.weapon || "pistol");
    vm.setWeapon(vmWeapon);
    vm.setVisible(alive && !driving);
    // own-fire recoil + muzzle flash: fire the anim when a replicated ammo
    // counter DROPS (reload/pickup only ever raise it; weapon swaps resync)
    if (vmWeapon === this.lastVmWeapon && alive) {
      if ((p.ammo ?? 0) < this.lastAmmo || (p.rocketAmmo ?? 0) < this.lastRocketAmmo) {
        vm.fire(WEAPONS[vmWeapon] ?? WEAPONS.pistol, 0);
      }
    }
    this.lastAmmo = p.ammo ?? 0;
    this.lastRocketAmmo = p.rocketAmmo ?? 0;
    this.lastVmWeapon = vmWeapon;
    const moving = alive && !driving && !!this.kin && Math.hypot(this.kin.vx, this.kin.vz) > 0.6;
    vm.update(dtSec, moving, this.kin?.onGround ?? true, this.adsHeld && alive, this.yaw, this.pitch, this.kin?.vy ?? 0);

    const status = !gp
      ? "🎮 CONTROLLER DISCONNECTED"
      : p.pending ? "DROPPING IN…"
      : p.downed ? "DOWNED"
      : !p.alive ? "ELIMINATED"
      : "";
    this.hud.sync(p, status);
  }

  dispose(): void {
    releasePad(this.conn.seat.padIndex); // menus between rounds read this pad
    this.hud.root.remove();
  }
}
